import { useEffect, useState } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  fetchAffirmations,
  fetchDailyAffirmation,
  generateAffirmations,
} from "../lib/api";

const BG_IMAGE =
  "https://images.unsplash.com/photo-1521194263619-39ecc5b55c61?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

export default function Affirmations() {
  const [curated, setCurated] = useState([]);
  const [daily, setDaily] = useState(null);
  const [mood, setMood] = useState("");
  const [intention, setIntention] = useState("");
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState([]);
  const [category, setCategory] = useState("All");

  useEffect(() => {
    fetchAffirmations().then(setCurated).catch(() => {});
    fetchDailyAffirmation().then(setDaily).catch(() => {});
  }, []);

  const categories = ["All", ...new Set(curated.map((c) => c.category))];
  const visible =
    category === "All" ? curated : curated.filter((c) => c.category === category);

  const onGenerate = async () => {
    setLoading(true);
    setGenerated([]);
    try {
      const res = await generateAffirmations({
        mood: mood.trim() || null,
        intention: intention.trim() || null,
        count: 4,
      });
      setGenerated(res.affirmations);
    } catch (e) {
      toast.error("Could not generate affirmations");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="affirmations-page"
      className="pt-32 md:pt-36 pb-24"
    >
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-20">
        <p className="font-eyebrow text-[11px] text-[#5C5C59]">
          Affirmations Library
        </p>
        <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl text-[#1A1A1A] mt-4 max-w-5xl">
          Soft truths,{" "}
          <em className="editorial-italic text-[#7B8871]">spoken slowly.</em>
        </h1>
        <p className="text-[#5C5C59] text-base md:text-lg leading-relaxed mt-8 max-w-2xl">
          Read what's curated, or share where you are today and receive a few
          affirmations written just for this moment.
        </p>
      </section>

      {/* Daily affirmation */}
      {daily && (
        <section className="relative px-6 md:px-12 lg:px-20 mt-16 md:mt-24">
          <div className="relative overflow-hidden border border-[#D8D4CC]">
            <img
              src={BG_IMAGE}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-[#F7F5F2]/60" />
            <div className="relative p-10 md:p-16">
              <p className="font-eyebrow text-[10px] text-[#5C5C59]">
                Today, {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              </p>
              <p
                data-testid="daily-affirmation"
                className="font-serif-display text-4xl md:text-6xl text-[#1A1A1A] mt-6 max-w-4xl leading-tight"
              >
                <em className="editorial-italic">"{daily.text}"</em>
              </p>
              <p className="font-eyebrow text-[10px] text-[#7B8871] mt-6">
                {daily.category}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* AI Generator */}
      <section
        data-testid="ai-generator-section"
        className="px-6 md:px-12 lg:px-20 mt-24 md:mt-32"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="font-eyebrow text-[11px] text-[#5C5C59]">
              Personalized
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#1A1A1A] mt-4">
              Written for{" "}
              <em className="editorial-italic text-[#7B8871]">today.</em>
            </h2>
            <p className="text-[#5C5C59] text-sm leading-relaxed mt-6">
              Tell us how you're feeling and what you're moving toward. We'll
              compose four affirmations shaped to this exact moment.
            </p>
          </div>

          <div className="lg:col-span-8">
            <div className="space-y-8">
              <div>
                <label className="font-eyebrow text-[10px] text-[#5C5C59]">
                  How are you feeling?
                </label>
                <input
                  data-testid="affirmation-mood-input"
                  className="input-underline"
                  placeholder="Tender, overwhelmed, hopeful, scattered…"
                  value={mood}
                  onChange={(e) => setMood(e.target.value)}
                />
              </div>
              <div>
                <label className="font-eyebrow text-[10px] text-[#5C5C59]">
                  Your intention or focus
                </label>
                <input
                  data-testid="affirmation-intention-input"
                  className="input-underline"
                  placeholder="Slowing down, trusting myself, returning home…"
                  value={intention}
                  onChange={(e) => setIntention(e.target.value)}
                />
              </div>
              <button
                data-testid="generate-affirmations-btn"
                onClick={onGenerate}
                disabled={loading}
                className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F7F5F2] px-8 py-4 font-eyebrow text-[11px] hover:bg-[#7B8871] transition-colors duration-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <RefreshCw strokeWidth={1.5} className="w-4 h-4 animate-spin" />
                    Composing…
                  </>
                ) : (
                  <>
                    <Sparkles strokeWidth={1.5} className="w-4 h-4" />
                    Write my affirmations
                  </>
                )}
              </button>
            </div>

            {/* Results */}
            {generated.length > 0 && (
              <div
                data-testid="generated-affirmations"
                className="mt-14 space-y-6 border-t border-[#D8D4CC] pt-10"
              >
                {generated.map((a, i) => (
                  <p
                    key={i}
                    data-testid={`generated-affirmation-${i}`}
                    className="font-serif-display text-2xl md:text-4xl text-[#1A1A1A] leading-tight blur-in"
                    style={{ animationDelay: `${i * 200}ms` }}
                  >
                    <em className="editorial-italic">"{a}"</em>
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Curated library */}
      <section className="px-6 md:px-12 lg:px-20 mt-32 md:mt-40">
        <div className="flex items-baseline justify-between flex-wrap gap-6 mb-10">
          <div>
            <p className="font-eyebrow text-[11px] text-[#5C5C59]">
              Curated Library
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#1A1A1A] mt-3">
              Read at your{" "}
              <em className="editorial-italic text-[#7B8871]">own pace.</em>
            </h2>
          </div>
        </div>

        <div
          data-testid="affirmation-categories"
          className="flex flex-wrap items-center gap-3 mb-12"
        >
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`category-${cat.toLowerCase()}`}
              onClick={() => setCategory(cat)}
              className={`px-4 py-2 font-eyebrow text-[10px] border transition-colors ${
                category === cat
                  ? "bg-[#1A1A1A] text-[#F7F5F2] border-[#1A1A1A]"
                  : "border-[#D8D4CC] text-[#5C5C59] hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {visible.map((a, i) => (
            <div
              key={a.id}
              data-testid={`curated-affirmation-${a.id}`}
              className="border-t border-[#D8D4CC] pt-6 fade-up"
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <p className="font-eyebrow text-[9px] text-[#7B8871]">
                {a.category}
              </p>
              <p className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] mt-4 leading-tight">
                <em className="editorial-italic">"{a.text}"</em>
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
